import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flame, Leaf, Globe, Sparkles, BookOpen, Map, Search, TrendingUp, Star, BrainCircuit, BookmarkCheck, Users } from 'lucide-react';
import { useTranslation } from "react-i18next";

// Import các components mới tạo
import { MobileCard } from '@/components/ui/mobile/MobileCard';
import { FilterPills } from '@/components/ui/mobile/FilterPills';
import MobileSearch from '@/components/ui/mobile/MobileSearch';
import ScrollableSection from '@/components/ui/mobile/ScrollableSection';
import MobileCarousel from '@/components/ui/mobile/MobileCarousel';
import CategoryPills from '@/components/ui/mobile/CategoryPills';
import TouchableCard from '@/components/ui/mobile/TouchableCard';

import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { SEO } from '@/components/ui/seo';

// Import services and utils
import { VERIFIED_IMAGES } from '@/services/imageService';
import { 
  getAllCourses, 
  getFeaturedCourses, 
  getPopularCourses,
  formatDuration,
  Course 
} from '@/services/courseService';
import { getAllCategories, Category } from '@/services/categoryService';
import { getDiagrams } from '@/services/DiagramService';
import { cn } from '@/lib/utils';

// Định nghĩa các interface
interface FilterItem {
  id: string;
  name: string;
  icon?: React.ReactNode;
}

// Helpers
const getColorForDifficulty = (difficulty?: string): "blue" | "green" | "orange" | "red" | "purple" | "yellow" => {
  switch (difficulty?.toLowerCase()) {
    case 'beginner': return 'green';
    case 'intermediate': return 'blue';
    case 'advanced': return 'orange';
    case 'expert': return 'red';
    default: return 'purple';
  }
};

const getCategoryColor = (category?: string): "blue" | "green" | "orange" | "red" | "purple" | "yellow" => {
  const categoryMap: Record<string, "blue" | "green" | "orange" | "red" | "purple" | "yellow"> = {
    'meditation': 'purple',
    'abhidhamma': 'blue',
    'early-buddhism': 'green',
    'mahayana': 'orange',
    'sutra': 'yellow',
    'history': 'red',
  };
  
  if (!category) return 'purple';
  
  return categoryMap[category.toLowerCase()] || 'blue';
};

const getDiagramColor = (index: number): "blue" | "green" | "purple" | "amber" | "red" | "indigo" => {
  const colors: Array<"blue" | "green" | "purple" | "amber" | "red" | "indigo"> = [
    'blue', 'purple', 'green', 'amber', 'red', 'indigo'
  ];
  return colors[index % colors.length];
};

const getDiagramIcon = (type: string | number): string => {
  if (typeof type === 'number') {
    const icons = [
      VERIFIED_IMAGES.wisdom,
      VERIFIED_IMAGES.meditation,
      VERIFIED_IMAGES.nature,
      VERIFIED_IMAGES.dharma,
      VERIFIED_IMAGES.lotus,
      VERIFIED_IMAGES.buddha
    ];
    return icons[type % icons.length];
  }
  
  const typeMap: Record<string, string> = {
    'four-noble-truths': VERIFIED_IMAGES.wisdom,
    'eightfold-path': VERIFIED_IMAGES.meditation,
    'five-aggregates': VERIFIED_IMAGES.nature,
    'three-marks': VERIFIED_IMAGES.dharma,
    'dependent-origination': VERIFIED_IMAGES.lotus,
    'default': VERIFIED_IMAGES.buddha
  };
  
  return typeMap[type] || typeMap.default;
};

const HomeMobile: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(["common"]);
  
  // State for data
  const [courses, setCourses] = useState<Course[]>([]);
  const [popularCourses, setPopularCourses] = useState<Course[]>([]);
  const [featuredCourses, setFeaturedCourses] = useState<Course[]>([]);
  const [continueLearningCourses, setContinueLearningCourses] = useState<Course[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [diagrams, setDiagrams] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // User state (mock for now)
  const isLoggedIn = false;
  
  // Effect to fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // Get categories and diagrams
        const cats = await getAllCategories();
        const allDiagrams = await getDiagrams();
        
        // Get courses
        const allCourses = await getAllCourses();
        const popular = await getPopularCourses();
        const featured = await getFeaturedCourses();
        
        setCourses(allCourses);
        setPopularCourses(popular);
        setFeaturedCourses(featured);
        setCategories(cats);
        setDiagrams(allDiagrams);
        
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Handle category selection
  const handleCategorySelect = (categoryId: string) => {
    navigate(`/courses/category/${categoryId}`);
  };
  
  // Handle card clicks
  const handleCardClick = (courseId: string) => {
    navigate(`/courses/${courseId}`);
  };

  return (
    <>
      <SEO title="Giác Ngộ - Nền tảng học tập Phật giáo trực tuyến" />
      
      <div className="pb-0">
        {/* Header và Welcome */}
        <div className="px-4 pt-2 pb-1">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className="text-xl font-bold">Chào mừng!</h1>
              <p className="text-gray-500 dark:text-gray-400 text-sm">Khám phá các sơ đồ và khoá học Phật học</p>
            </div>
            <Button className="h-10 w-10 rounded-full p-0" variant="ghost" onClick={() => navigate('/search')}>
              <Search className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Featured Carousel */}
        <div className="mb-3 px-4">
          <MobileCarousel 
            className="featured-carousel"
            pagination={true}
            items={featuredCourses.map((course, index) => (
              <div key={index} className="h-[200px] relative rounded-xl overflow-hidden" onClick={() => navigate(`/courses/${course.id}`)}>
                <img 
                  src={course.image} 
                  alt={course.name || `Featured Course ${index + 1}`}
                  className="w-full h-full object-cover" 
                />
                {/* Overlay nhẹ */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                {/* Text container ở dưới */}
                <div className="absolute bottom-0 left-0 right-0 p-2">
                  <div className="bg-black/80 rounded-t-lg px-3 py-2">
                    <h3 className="featured-carousel-title text-white text-xl font-bold">
                      {course.name}
                    </h3>
                  </div>
                </div>
              </div>
            ))}
          />
        </div>

        {/* Quick Access Widgets */}
        <div className="px-4 mb-3">
          <div className="grid grid-cols-4 gap-2">
            <TouchableCard
              onClick={() => navigate('/courses')}
              className="aspect-square flex flex-col items-center justify-center space-y-1 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg p-2"
            >
              <BookOpen className="h-6 w-6 text-blue-500 dark:text-blue-400" />
              <span className="text-[10px] font-medium text-center">Khóa học</span>
            </TouchableCard>
            
            <TouchableCard
              onClick={() => navigate('/diagrams')}
              className="aspect-square flex flex-col items-center justify-center space-y-1 bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800 rounded-lg p-2"
            >
              <Map className="h-6 w-6 text-purple-500 dark:text-purple-400" />
              <span className="text-[10px] font-medium text-center">Sơ đồ</span>
            </TouchableCard>
            
            <TouchableCard
              onClick={() => navigate('/meditation')}
              className="aspect-square flex flex-col items-center justify-center space-y-1 bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800 rounded-lg p-2"
            >
              <Sparkles className="h-6 w-6 text-green-500 dark:text-green-400" />
              <span className="text-[10px] font-medium text-center">Thiền</span>
            </TouchableCard>
            
            <TouchableCard
              onClick={() => navigate('/news')}
              className="aspect-square flex flex-col items-center justify-center space-y-1 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800 rounded-lg p-2"
            >
              <Star className="h-6 w-6 text-amber-500 dark:text-amber-400" />
              <span className="text-[10px] font-medium text-center">Tin tức</span>
            </TouchableCard>
          </div>
        </div>
        
        {/* Continue Learning */}
        {isLoggedIn && continueLearningCourses.length > 0 && (
          <div className="px-6 mb-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-bold">Tiếp tục học</h2>
              <Link to="/learning-dashboard" className="text-sm text-primary">Xem tất cả</Link>
            </div>
            {/* Continue Learning cards */}
            {continueLearningCourses.map((course, index) => (
              <MobileCard
                key={course.id || index}
                id={course.id}
                title={course.name}
                description={course.description || ''}
                image={course.image}
                color={getColorForDifficulty(course.difficulty)}
                duration={formatDuration(course.estimated_time || '')}
                author={course.expert_details?.name || ''}
                onClick={() => navigate(`/courses/${course.id}`)}
                progress={0} // TODO: Get from user progress
                level={course.difficulty || 'beginner'}
                category={typeof course.category === 'string' ? course.category : 'Phật học'}
                lessons={course.total_lessons || 0}
                rating={0}
              />
            ))}
          </div>
        )}

        {/* Popular courses */}
        <div className="px-4 mb-3">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold">Khóa học phổ biến</h2>
            <Link to="/courses" className="text-sm text-primary">Xem tất cả</Link>
          </div>
          {/* Popular courses list */}
          {popularCourses.map((course, index) => (
            <MobileCard
              key={course.id || index}
              id={course.id}
              title={course.name}
              description={course.description || ''}
              image={course.image || VERIFIED_IMAGES.placeholder}
              color={getColorForDifficulty(course.difficulty)}
              duration={formatDuration(course.estimated_time || '')}
              author={course.expert_details?.name || ''}
              onClick={() => navigate(`/courses/${course.id}`)}
              rating={course.rating || 0}
              studentsCount={course.students_count || 0}
              level={course.difficulty || 'beginner'}
              category={typeof course.category === 'object' && course.category?.name ? course.category.name : 'Phật học'}
              categoryColor={getCategoryColor(typeof course.category === 'object' && course.category?.name ? course.category.name : '')}
              lessons={course.total_lessons || 0}
            />
          ))}
        </div>

        {/* Categories */}
        <div className="px-4 mb-3">
          <h2 className="text-lg font-bold mb-2">Danh mục</h2>
          <CategoryPills 
            categories={categories}
            onSelect={handleCategorySelect}
          />
        </div>

        {/* Diagrams Section */}
        <ScrollableSection
          title="Sơ đồ Phật học"
          viewAllLink="/diagrams"
          contentClassName="space-x-3"
          className="my-3"
        >
          {isLoading ? (
            // Loading skeleton
            Array(4).fill(null).map((_, index) => (
              <div key={index} className="flex-shrink-0 w-[160px] h-28 rounded-xl bg-gray-200 dark:bg-gray-800 animate-pulse"></div>
            ))
          ) : diagrams.length > 0 ? (
            // Hiển thị diagram từ database
            diagrams.slice(0, 5).map((diagram, index) => (
              <TouchableCard
                key={diagram.id || index}
                onClick={() => navigate(`/diagrams/${diagram.id}`)}
                className="flex-shrink-0 w-[160px] h-28 rounded-xl overflow-hidden relative shadow-md cursor-pointer snap-start"
              >
                <div className={`w-full h-full bg-gradient-to-br from-${getDiagramColor(index)}-600 to-${getDiagramColor(index)}-400 flex items-center justify-center`}>
                  <img 
                    src={getDiagramIcon(diagram.type || index)}
                    alt={diagram.name} 
                    className="w-16 h-16 opacity-30 absolute"
                  />
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <p className="text-white font-medium text-center text-sm px-2">{diagram.name}</p>
                </div>
              </TouchableCard>
            ))
          ) : (
            // Nếu không có dữ liệu từ API thì hiển thị các card tĩnh
            <>
              <TouchableCard
                onClick={() => navigate('/diagrams/four-noble-truths')}
                className="flex-shrink-0 w-[160px] h-28 rounded-xl overflow-hidden relative shadow-md cursor-pointer snap-start"
              >
                <div className="w-full h-full bg-gradient-to-br from-blue-600 to-blue-400 flex items-center justify-center">
                  <img 
                    src={VERIFIED_IMAGES.wisdom}
                    alt="Tứ Diệu Đế" 
                    className="w-16 h-16 opacity-30 absolute"
                  />
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <p className="text-white font-medium text-center text-sm px-2">Tứ Diệu Đế</p>
                </div>
              </TouchableCard>
              
              <TouchableCard
                onClick={() => navigate('/diagrams/eightfold-path')}
                className="flex-shrink-0 w-[160px] h-28 rounded-xl overflow-hidden relative shadow-md cursor-pointer snap-start"
              >
                <div className="w-full h-full bg-gradient-to-br from-purple-600 to-purple-400 flex items-center justify-center">
                  <img 
                    src={VERIFIED_IMAGES.meditation}
                    alt="Bát Chánh Đạo" 
                    className="w-16 h-16 opacity-30 absolute"
                  />
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <p className="text-white font-medium text-center text-sm px-2">Bát Chánh Đạo</p>
                </div>
              </TouchableCard>
              
              <TouchableCard
                onClick={() => navigate('/diagrams/five-aggregates')}
                className="flex-shrink-0 w-[160px] h-28 rounded-xl overflow-hidden relative shadow-md cursor-pointer snap-start"
              >
                <div className="w-full h-full bg-gradient-to-br from-green-600 to-green-400 flex items-center justify-center">
                  <img 
                    src={VERIFIED_IMAGES.nature}
                    alt="Ngũ Uẩn" 
                    className="w-16 h-16 opacity-30 absolute"
                  />
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <p className="text-white font-medium text-center text-sm px-2">Ngũ Uẩn</p>
                </div>
              </TouchableCard>
              
              <TouchableCard
                onClick={() => navigate('/diagrams/three-marks')}
                className="flex-shrink-0 w-[160px] h-28 rounded-xl overflow-hidden relative shadow-md cursor-pointer snap-start"
              >
                <div className="w-full h-full bg-gradient-to-br from-amber-600 to-amber-400 flex items-center justify-center">
                  <img 
                    src={VERIFIED_IMAGES.dharma}
                    alt="Tam Tướng" 
                    className="w-16 h-16 opacity-30 absolute"
                  />
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <p className="text-white font-medium text-center text-sm px-2">Tam Tướng</p>
                </div>
              </TouchableCard>
              
              <TouchableCard
                onClick={() => navigate('/diagrams/dependent-origination')}
                className="flex-shrink-0 w-[160px] h-28 rounded-xl overflow-hidden relative shadow-md cursor-pointer snap-start"
              >
                <div className="w-full h-full bg-gradient-to-br from-red-600 to-red-400 flex items-center justify-center">
                  <img 
                    src={VERIFIED_IMAGES.lotus}
                    alt="Thập Nhị Nhân Duyên" 
                    className="w-16 h-16 opacity-30 absolute"
                  />
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <p className="text-white font-medium text-center text-sm px-2">Thập Nhị Nhân Duyên</p>
                </div>
              </TouchableCard>
            </>
          )}
        </ScrollableSection>

        {/* Top courses by category */}
        <ScrollableSection
          title="Khóa học theo chủ đề"
          viewAllLink="/courses"
          contentClassName="space-x-4"
        >
          {courses.map((course, index) => (
            <MobileCard
              key={course.id || index}
              id={course.id}
              title={course.name}
              description={course.description || ''}
              image={course.image}
              color={getColorForDifficulty(course.difficulty)}
              duration={formatDuration(course.estimated_time || '')}
              author={course.expert_details?.name || ''}
              onClick={() => navigate(`/courses/${course.id}`)}
              className="flex-shrink-0 w-[230px] snap-start"
              level={course.difficulty || 'beginner'} 
              category={typeof course.category === 'string' ? course.category : 'Phật học'}
              lessons={course.total_lessons || 0}
              rating={course.rating || 0}
              studentsCount={course.students_count || 0}
            />
          ))}
        </ScrollableSection>

        {/* Explore All Features */}
        <div className="px-6 mt-2 mb-4">
          <TouchableCard
            className="bg-gradient-to-r from-indigo-600 to-blue-600 p-4 rounded-xl text-white shadow-md"
            onClick={() => navigate('/learning-dashboard')}
          >
            <div className="flex items-center">
              <div className="flex-1">
                <h3 className="font-bold text-lg">Hành trình Phật học</h3>
                <p className="text-white/90 text-sm mt-1 line-clamp-2">Khám phá đầy đủ các tính năng học tập của Giác Ngộ</p>
              </div>
              <div className="h-12 w-12 bg-white/20 rounded-full flex items-center justify-center">
                <BookmarkCheck className="h-6 w-6 text-white" />
              </div>
            </div>
          </TouchableCard>
        </div>

        {/* Thêm phần tin tức ngắn ở cuối */}
        <div className="px-6 mb-8">
          <h2 className="text-lg font-bold mb-2">Tin tức Phật giáo</h2>
          <TouchableCard
            className="bg-white dark:bg-gray-800 p-3 rounded-xl border shadow-sm"
            onClick={() => navigate('/news')}
          >
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0">
                <img 
                  src={VERIFIED_IMAGES.temple}
                  alt="Tin tức Phật giáo" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-sm line-clamp-2">Cập nhật các tin tức Phật giáo mới nhất</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Bài viết, thông báo và sự kiện</p>
              </div>
            </div>
          </TouchableCard>
        </div>
      </div>
    </>
  );
};

export default HomeMobile; 